/**
 * URL normalization utilities for media URLs
 * Extracted from S3FilePreview to reduce bundle size
 */

import { MEDIA_CACHE_VERSION } from '@/config/media-config';

/**
 * Create canonical URL for deduplication
 * Removes version parameters and normalizes to a consistent format
 * This ensures URLs with different query params map to the same cache key
 * CRITICAL: Use this for all caching, preloading, and deduplication to prevent duplicate downloads
 */
export const getCanonicalMediaUrl = (url: string | null | undefined): string | null | undefined => {
  if (!url) return url;

  // Normalize /api/v1/media/download to /api/media/download
  let normalized = url;
  if (normalized.includes('/api/v1/media/download')) {
    normalized = normalized.replace('/api/v1/media/download', '/api/media/download');
  }

  // For /api/media/download URLs, extract the S3 key and create canonical URL
  // Remove version parameters and other query params for deduplication
  if (normalized.includes('/api/media/download')) {
    try {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const urlObj = new URL(normalized, baseUrl);
      const keyParam = urlObj.searchParams.get('key');
      const viewParam = urlObj.searchParams.get('view');
      
      if (keyParam) {
        // Create canonical URL with just the key and view=true (no version param)
        // This ensures all variations of the same file use the same cache key
        return `/api/media/download?key=${encodeURIComponent(keyParam)}&view=${viewParam || 'true'}`;
      }
    } catch (_error) {
      // If URL parsing fails, return normalized URL as-is
    }
  }

  return normalized;
};

/**
 * Normalize /api/v1/media/download URLs to /api/media/download
 * and ensure cache version parameter is present
 * 
 * NOTE: For deduplication/caching, use getCanonicalMediaUrl instead
 * This function is for URLs that need version parameters for cache-busting
 */
export const normalizeMediaUrl = (url: string | null | undefined): string | null | undefined => {
  if (!url) return url;

  // Use canonical URL as base (removes version params)
  const canonical = getCanonicalMediaUrl(url);
  if (!canonical) return url;

  // If it's a media download URL, add version parameter back
  if (canonical.includes('/api/media/download')) {
    try {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const urlObj = new URL(canonical, baseUrl);
      const versionParam = urlObj.searchParams.get('v');

      // If version is missing or old, update it to current version
      if (!versionParam || versionParam !== MEDIA_CACHE_VERSION) {
        urlObj.searchParams.set('v', MEDIA_CACHE_VERSION);
        return urlObj.pathname + urlObj.search;
      }
      return canonical;
    } catch (_error) {
      // If URL parsing fails, try to append version manually
      if (!canonical.includes('&v=') && !canonical.includes('?v=')) {
        const separator = canonical.includes('?') ? '&' : '?';
        return `${canonical}${separator}v=${MEDIA_CACHE_VERSION}`;
      }
      return canonical;
    }
  }

  return canonical;
};

/**
 * Extract S3 key from media download URL
 */
export const extractS3KeyFromUrl = (url: string): string => {
  if (url.includes('/api/media/download') || url.includes('/api/v1/media/download')) {
    try {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const urlObj = new URL(url, baseUrl);
      const keyParam = urlObj.searchParams.get('key');
      if (keyParam) {
        // Remove query parameters from the key itself if present (e.g., "photo-123?w=1920" -> "photo-123")
        return keyParam.split('?')[0].split('&')[0];
      }
    } catch (_error) {
      // If URL parsing fails, use original src
    }
  }
  return url;
};

/**
 * Generate responsive image URL with width parameter
 * This sets up the structure for server-side image resizing
 * @param url - Original media URL
 * @param width - Desired width in pixels
 * @returns URL with width parameter added
 */
export const generateResponsiveImageUrl = (url: string, width: number): string => {
  if (!url || !url.includes('/api/media/download')) {
    return url;
  }

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const urlObj = new URL(url, baseUrl);
    
    // Add width parameter for responsive images
    urlObj.searchParams.set('w', width.toString());
    
    return urlObj.pathname + urlObj.search;
  } catch (_error) {
    // If URL parsing fails, return original
    return url;
  }
};

/**
 * Generate srcset for responsive images
 * Creates multiple image URLs with different widths for the browser to choose from
 * @param url - Original media URL
 * @param widths - Array of widths in pixels (e.g., [320, 640, 1280])
 * @returns srcset string (e.g., "url?w=320 320w, url?w=640 640w")
 */
export const generateImageSrcSet = (url: string, widths: number[]): string => {
  if (!url) return '';
  
  return widths
    .map(width => `${generateResponsiveImageUrl(url, width)} ${width}w`)
    .join(', ');
};

